/**
 * api.js — Backend API client.
 *
 * Provides a single `apiFetch` helper that:
 *   1. Reads the user's Supabase access token from the current session
 *   2. Attaches it as a Bearer token in the Authorization header
 *   3. Calls the NDORFlow backend (/api/...)
 *   4. Parses and returns the JSON response
 *
 * All backend endpoints require authentication, so every call goes
 * through this helper.
 *
 * RC 1.44.H2 (2026-04-30) — P1 Session-resilience.
 *   • Silent-refresh path on 401. If the backend returns a 401, we
 *     ask Supabase to refresh the session and transparently retry
 *     the request ONCE with the new token. This closes the long-
 *     standing symptom where users saw a cryptic "Database query
 *     failed" after ~1 h because the JWT expired.
 *   • Session-expired broadcast. If the refresh itself fails (user
 *     revoked access, device offline, etc.) we fire a custom DOM
 *     event `ndorflow:session-expired` on window. A global listener
 *     in the shell shows the `SessionExpiredModal` and stops burning
 *     retries in the background.
 *   • Backend structured-detail awareness. The backend now returns
 *     `{ code, status, method, path, reason }` on Supabase errors.
 *     We normalise `err.message` to a human string but keep the full
 *     payload on `err.data.detail` so callers can branch on
 *     `err.data.detail.code === 'SESSION_EXPIRED'` without parsing
 *     strings.
 *
 * PLATFORM NOTE — preview telemetry:
 *   Some preview/staging environments inject a telemetry layer that
 *   eagerly consumes native fetch() response body streams. By the time
 *   user-land code calls `.text()` or `.json()`, the stream has
 *   already been drained and `res.clone()` cannot rescue it (it
 *   shares the same underlying consumer). We therefore route backend
 *   HTTP calls through XMLHttpRequest — the same workaround
 *   `lib/supabase.js` uses on the SDK side.
 */

import { supabase } from './supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** XHR-backed fetch. Immune to preview telemetry stream-consumption. */
function xhrRequest(url, { method, headers, body }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method || 'GET', url);
    Object.entries(headers || {}).forEach(([k, v]) =>
      xhr.setRequestHeader(k, v),
    );
    xhr.onload = () => {
      resolve({
        status: xhr.status,
        ok: xhr.status >= 200 && xhr.status < 300,
        text: xhr.responseText,
      });
    };
    xhr.onerror = () => reject(new TypeError('Network request failed'));
    xhr.send(body || null);
  });
}

/**
 * Fire the session-expired broadcast. A global listener in the shell
 * (`App.js` → `SessionExpiredModal`) opens the modal, which in turn
 * offers the user a "Sign in again" CTA that clears Supabase + navigates
 * to /login.
 *
 * Guarded by a module-level flag so a burst of 401s (common during a
 * refresh storm) doesn't fire dozens of events.
 */
let _sessionExpiredFired = false;
function broadcastSessionExpired() {
  if (_sessionExpiredFired) return;
  _sessionExpiredFired = true;
  try {
    window.dispatchEvent(new Event('ndorflow:session-expired'));
  } catch {
    /* swallow — not worth crashing over */
  }
  // Reset the flag after a grace window so a genuinely separate
  // expiry later in the session still notifies.
  setTimeout(() => { _sessionExpiredFired = false; }, 30_000);
}

/**
 * Single-flight refresh promise so concurrent 401s don't trigger
 * multiple Supabase refresh calls in parallel (which race and can
 * end up invalidating each other).
 */
let _refreshInFlight = null;
async function refreshSupabaseToken() {
  if (!_refreshInFlight) {
    _refreshInFlight = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data?.session?.access_token) {
          return null;
        }
        return data.session.access_token;
      } catch {
        return null;
      } finally {
        // Clear the cached promise so the next 401 (if any) starts a
        // fresh refresh cycle.
        setTimeout(() => { _refreshInFlight = null; }, 500);
      }
    })();
  }
  return _refreshInFlight;
}

/**
 * Make an authenticated request to the NDORFlow backend.
 *
 * @param {string} path   — API path, e.g. "/api/jobs"
 * @param {object} opts   — fetch options (method, body, etc.)
 * @returns {Promise<any>} — parsed JSON response
 * @throws {Error}         — with the server's error message;
 *                           `err.status`, `err.data`, and
 *                           `err.code` are attached.
 */
export async function apiFetch(path, opts = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    broadcastSessionExpired();
    const e = new Error('Not authenticated. Please sign in.');
    e.status = 401;
    e.code = 'SESSION_EXPIRED';
    throw e;
  }

  const isFormData =
    (typeof FormData !== 'undefined') && (opts.body instanceof FormData);

  let body = null;
  if (opts.body !== undefined && opts.body !== null) {
    if (isFormData) {
      body = opts.body;
    } else if (typeof opts.body === 'string') {
      body = opts.body;
    } else {
      body = JSON.stringify(opts.body);
    }
  }

  const buildHeaders = (token) => {
    const h = { ...(opts.headers || {}), Authorization: `Bearer ${token}` };
    if (!isFormData && !h['Content-Type'] && !h['content-type']) {
      h['Content-Type'] = 'application/json';
    }
    return h;
  };

  let res = await xhrRequest(`${BACKEND_URL}${path}`, {
    method: opts.method || 'GET',
    headers: buildHeaders(session.access_token),
    body,
  });

  // Silent refresh + single retry on 401.
  if (res.status === 401 && !opts._skipRefresh) {
    const newToken = await refreshSupabaseToken();
    if (newToken) {
      res = await xhrRequest(`${BACKEND_URL}${path}`, {
        method: opts.method || 'GET',
        headers: buildHeaders(newToken),
        body,
      });
    } else {
      broadcastSessionExpired();
    }
  }

  let parsed = null;
  if (res.text) {
    try { parsed = JSON.parse(res.text); } catch { parsed = null; }
  }

  if (!res.ok) {
    // RC 1.44.H2 — backend may return structured `{code, status,
    // method, path, reason}` under `detail`. Extract a human message
    // but keep the whole payload on err.data + surface the code.
    const detail = parsed?.detail;
    let message;
    let code = null;
    if (detail && typeof detail === 'object') {
      code = detail.code || null;
      message = detail.reason || detail.code || `HTTP ${res.status}`;
      if (code === 'SESSION_EXPIRED') {
        // The retry above already attempted a refresh; if we still
        // land here with SESSION_EXPIRED, broadcast and let the modal
        // take over.
        broadcastSessionExpired();
        message = 'Your session has expired. Please sign in again.';
      }
    } else {
      message = (typeof detail === 'string' && detail)
        || res.text
        || `HTTP ${res.status}`;
    }

    const err = new Error(
      typeof message === 'string' ? message : JSON.stringify(message),
    );
    err.status = res.status;
    err.code = code;
    if (parsed && typeof parsed === 'object') err.data = parsed;
    throw err;
  }

  return parsed;
}
